/**
 * UpsellOpportunities — the Upsell Intelligence recommendations, on their own page.
 *
 * These used to sit as a panel on every project page, where they were noise on a
 * surface people open to run the project, not to sell into it. Same engine, same
 * component (<SmartUpsellPanel>, driven by /api/upsell-intelligence/*) — you now
 * pick the project instead of the project deciding for you.
 *
 * The selection lives in the URL (`?program=<id>`), so a specific project's
 * opportunities can be linked or bookmarked.
 *
 * Lives at /upsells/opportunities, listed in the sidebar under Sales. Not to be
 * confused with /upsells itself, which is the PIPELINE for upsells that have
 * already been proposed — this page is what to propose next.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, Sparkles, ArrowUpRight } from "lucide-react";
import { apiClient } from "../App";
import { Input } from "../components/ui/input";
import { SmartUpsellPanel } from "../components/SmartUpsellPanel";

// Projects that can't be sold into aren't worth listing. Closed work can still
// carry an upsell (a past client buying a video), so only archived is excluded.
const isSellable = (p) => (p.status || "").toLowerCase() !== "archived";

export default function UpsellOpportunities() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const selectedId = searchParams.get("program");

  useEffect(() => {
    let mounted = true;
    apiClient
      .get("/programs")
      .then((res) => {
        if (!mounted) return;
        const list = (Array.isArray(res.data) ? res.data : res.data?.programs || []).filter(isSellable);
        list.sort((a, b) => (a.program_name || "").localeCompare(b.program_name || ""));
        setPrograms(list);
      })
      .catch((e) => console.error("Failed to load projects", e))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return programs;
    return programs.filter(
      (p) =>
        (p.program_name || "").toLowerCase().includes(q) ||
        (p.client_name || "").toLowerCase().includes(q)
    );
  }, [programs, query]);

  const selected = programs.find((p) => p.id === selectedId) || null;

  const select = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("program", id);
    else next.delete("program");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="upsell-opportunities">
      <div className="mb-6">
        <h1 className="text-[28px] font-light leading-tight text-foreground" style={{ fontFamily: "var(--font-sans)" }}>
          Upsell Opportunities
        </h1>
        <p className="mt-1 text-[13px] text-muted-foreground">
          What to propose next, ranked from what each project already has built.{" "}
          <Link to="/upsells" className="inline-flex items-center gap-0.5 text-foreground hover:underline">
            Proposed upsells live in the pipeline
            <ArrowUpRight className="h-3 w-3" />
          </Link>
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[280px_1fr]">
        {/* ── Pick a project ── */}
        <div className="rounded-2xl border border-border p-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search projects..."
              className="h-9 pl-9 text-[13px]"
              data-testid="upsell-project-search"
            />
          </div>

          {loading ? (
            <div className="mt-3 space-y-1.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="h-9 animate-pulse rounded-lg bg-secondary" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <p className="mt-4 text-[13px] text-muted-foreground">No projects match.</p>
          ) : (
            <div className="mt-3 max-h-[560px] space-y-0.5 overflow-y-auto">
              {filtered.map((p) => (
                <button
                  key={p.id}
                  onClick={() => select(p.id)}
                  className={`w-full rounded-lg px-2.5 py-2 text-left transition-colors ${
                    p.id === selectedId ? "bg-secondary" : "hover:bg-secondary/60"
                  }`}
                  data-testid={`upsell-project-${p.id}`}
                >
                  <p className="truncate text-[13px] font-medium text-foreground">{p.program_name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {p.client_name || "—"}
                    {p.stage ? ` · ${p.stage}` : ""}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Its opportunities ── */}
        <div>
          {selected ? (
            <SmartUpsellPanel
              key={selected.id}
              programId={selected.id}
              programName={selected.program_name}
            />
          ) : (
            <div
              className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-border px-6 text-center"
              data-testid="upsell-empty-state"
            >
              <Sparkles className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
              <p className="mt-3 text-[13px] font-medium text-foreground">Pick a project</p>
              <p className="mt-1 max-w-sm text-[13px] text-muted-foreground">
                Recommendations are ranked from what a project has already built — its models,
                renders, tours and platform — so they're per project.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
